/**
 * PermissionGate — render children only when the actor holds a permission.
 *
 * Used by: every action button and column in the Admin app ("Tạo chi nhánh",
 * "Xem giá vốn", "Thu hồi thiết bị"), the POS discount/void buttons and the
 * Staff manager-only tiles.
 *
 * ==> HIDING A BUTTON IS NOT SECURITY. <==
 *
 * The permission list comes from `GET /api/v1/auth/me` and exists purely so the
 * UI does not offer actions that will fail. The API re-derives permissions and
 * data scope on EVERY request (`requirePermission` + `ScopedDb` in
 * apps/api-worker/src/middleware), so a user who forges the client state, edits
 * the bundle, or calls the endpoint directly gets a 403 exactly the same. Never
 * move an authorisation decision into this component.
 *
 * The permission set is supplied through context by the app's AuthProvider so
 * call sites stay one line.
 */
import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

export interface PermissionContextValue {
  /** Effective permissions for the ACTIVE branch. */
  readonly permissions: ReadonlySet<string>;
  /** Per-branch map, for screens that render several branches at once. */
  readonly permissionsByBranch: Readonly<Record<string, ReadonlySet<string>>>;
  readonly activeBranchId: string | null;
}

const EMPTY: PermissionContextValue = {
  permissions: new Set<string>(),
  permissionsByBranch: {},
  activeBranchId: null,
};

const PermissionContext = createContext<PermissionContextValue>(EMPTY);

export function PermissionProvider({
  value,
  children,
}: {
  readonly value: PermissionContextValue;
  readonly children: ReactNode;
}): React.JSX.Element {
  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export interface UsePermissions {
  /** Holds the permission in the active branch (or in `branchId` if given). */
  has: (permission: string, branchId?: string | null) => boolean;
  /** Holds at least one of them. */
  hasAny: (permissions: readonly string[], branchId?: string | null) => boolean;
  /** Holds all of them. */
  hasAll: (permissions: readonly string[], branchId?: string | null) => boolean;
  readonly permissions: ReadonlySet<string>;
  readonly activeBranchId: string | null;
}

export function usePermissions(): UsePermissions {
  const ctx = useContext(PermissionContext);

  const resolve = useCallback(
    (branchId?: string | null): ReadonlySet<string> => {
      if (branchId === undefined || branchId === null) return ctx.permissions;
      return ctx.permissionsByBranch[branchId] ?? new Set<string>();
    },
    [ctx],
  );

  return useMemo(
    () => ({
      has: (permission, branchId) => resolve(branchId).has(permission),
      hasAny: (permissions, branchId) => {
        const set = resolve(branchId);
        return permissions.some((p) => set.has(p));
      },
      hasAll: (permissions, branchId) => {
        const set = resolve(branchId);
        return permissions.every((p) => set.has(p));
      },
      permissions: ctx.permissions,
      activeBranchId: ctx.activeBranchId,
    }),
    [ctx, resolve],
  );
}

export interface PermissionGateProps {
  /** A single permission code, or a list. */
  readonly permission?: string;
  readonly anyOf?: readonly string[];
  readonly allOf?: readonly string[];
  /** Check against a specific branch instead of the active one. */
  readonly branchId?: string | null;
  /** Rendered when the check fails. Default: nothing at all. */
  readonly fallback?: ReactNode;
  readonly children: ReactNode;
}

export function PermissionGate({
  permission,
  anyOf,
  allOf,
  branchId,
  fallback = null,
  children,
}: PermissionGateProps): React.JSX.Element {
  const perms = usePermissions();

  let allowed = true;
  if (permission !== undefined) allowed = allowed && perms.has(permission, branchId);
  if (anyOf !== undefined) allowed = allowed && perms.hasAny(anyOf, branchId);
  if (allOf !== undefined) allowed = allowed && perms.hasAll(allOf, branchId);

  return <>{allowed ? children : fallback}</>;
}
